import React, { useState, useEffect } from 'react'
import { X, Save, Menu, GripVertical, AlertTriangle } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { menuService, MenuItem } from '../../services/menuService'
import toast from 'react-hot-toast'

interface MenuOrderModalProps {
  isOpen: boolean
  onClose: () => void
  menus: MenuItem[]
}

const MenuOrderModal: React.FC<MenuOrderModalProps> = ({ isOpen, onClose, menus }) => {
  const queryClient = useQueryClient()
  const [menuItems, setMenuItems] = useState<MenuItem[]>([])
  const [parentId, setParentId] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  // Organizamos los menús por jerarquía
  useEffect(() => {
    if (isOpen && menus?.length) {
      // Filtrar inicialmente por menús raíz (sin parent_id)
      const rootMenus = menus
        .filter(menu => !menu.parent_id)
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      
      setMenuItems(rootMenus)
      setParentId(null)
      setHasChanges(false)
    }
  }, [isOpen, menus])

  const getChildMenus = (parentId: number) => {
    return menus
      .filter(menu => menu.parent_id === parentId)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
  }

  const handleDragStart = (e: React.DragEvent, index: number) => {
    e.dataTransfer.setData('index', index.toString())
    e.currentTarget.classList.add('opacity-50')
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    e.currentTarget.classList.add('bg-gray-100')
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.currentTarget.classList.remove('bg-gray-100')
  }

  const handleDragEnd = (e: React.DragEvent) => {
    e.currentTarget.classList.remove('opacity-50')
  }

  const handleDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault()
    e.currentTarget.classList.remove('bg-gray-100')
    
    const sourceIndex = parseInt(e.dataTransfer.getData('index'))
    if (sourceIndex === targetIndex) return

    const updatedMenus = [...menuItems]
    const [movedItem] = updatedMenus.splice(sourceIndex, 1)
    updatedMenus.splice(targetIndex, 0, movedItem)

    // Actualizar los sort_order de los elementos
    const newMenus = updatedMenus.map((item, idx) => ({
      ...item,
      sort_order: idx + 1
    }))

    setMenuItems(newMenus)
    setHasChanges(true)
  }

  const navigateToParent = (id: number | null) => {
    if (id === null) {
      const rootMenus = menus
        .filter(menu => !menu.parent_id)
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      
      setMenuItems(rootMenus)
      setParentId(null)
    } else {
      const childMenus = getChildMenus(id)
      setMenuItems(childMenus)
      setParentId(id)
    }
    
    setHasChanges(false)
  }

  const getParentPath = () => {
    if (!parentId) return []
    
    const path: { id: number, name: string }[] = []
    let currentId: number | null = parentId
    
    while (currentId) {
      const menu = menus.find(m => m.id === currentId)
      if (menu) {
        path.unshift({ id: menu.id, name: menu.name })
        currentId = menu.parent_id ?? null
      } else {
        break
      }
    }
    
    return path
  }

  const updateMenuOrdersMutation = useMutation({
    mutationFn: async () => {
      setSaving(true)
      
      // Actualizar el orden de cada menú
      const updatePromises = menuItems.map((menu, index) => 
        menuService.updateMenuOrder(menu.id, index + 1)
      )
      
      await Promise.all(updatePromises)
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['menus'])
      queryClient.invalidateQueries(['menus-test-data'])
      queryClient.invalidateQueries(['user-menus-sidebar'])
      toast.success('Orden de menús actualizado exitosamente')
      setSaving(false)
      setHasChanges(false)
    },
    onError: (error: any) => {
      toast.error(error.message || 'Error al actualizar el orden de los menús')
      setSaving(false)
    }
  })

  const handleSave = async () => {
    if (hasChanges) {
      await updateMenuOrdersMutation.mutateAsync()
    } else {
      onClose()
    }
  }

  if (!isOpen) return null

  const parentPath = getParentPath()

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <div className="h-8 w-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
              <Menu className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Ordenar Menús
              </h2>
              <p className="text-sm text-gray-500">
                Arrastra y suelta para reordenar los elementos del menú
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6" style={{ maxHeight: 'calc(90vh - 180px)' }}>
          {/* Breadcrumb */}
          <div className="flex items-center mb-4 text-sm">
            <button 
              onClick={() => navigateToParent(null)}
              className="text-primary-600 hover:text-primary-800"
            >
              Menú Principal
            </button>
            
            {parentPath.map((item) => (
              <React.Fragment key={item.id}>
                <span className="mx-2 text-gray-400">/</span>
                <button 
                  onClick={() => navigateToParent(item.id)}
                  className="text-primary-600 hover:text-primary-800"
                >
                  {item.name}
                </button>
              </React.Fragment>
            ))}
          </div>
          
          {/* Reordering list */}
          <div className="space-y-2">
            {menuItems.length > 0 ? (
              menuItems.map((item, index) => (
                <div
                  key={item.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragEnd={handleDragEnd}
                  onDrop={(e) => handleDrop(e, index)}
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-lg cursor-move bg-white hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center flex-1">
                    <div className="mr-3 text-gray-400">
                      <GripVertical className="h-5 w-5" />
                    </div>
                    <div className="flex items-center">
                      <div className="h-8 w-8 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                        <Menu className="h-4 w-4 text-gray-600" />
                      </div>
                      
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{item.name}</h3>
                        <div className="flex items-center text-xs text-gray-500">
                          <span className="mr-3">Orden: {item.sort_order}</span>
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
                            item.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {item.is_active ? 'Activo' : 'Inactivo'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* If item has children, add button to navigate to them */}
                  {menus.some(m => m.parent_id === item.id) && (
                    <button
                      onClick={() => navigateToParent(item.id)}
                      className="text-blue-600 hover:text-blue-800 ml-3 px-2 py-1 text-sm"
                    >
                      Ver submenús
                    </button>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>No hay elementos para mostrar en este nivel</p>
              </div>
            )}
          </div>

          {hasChanges && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start">
                <AlertTriangle className="h-5 w-5 text-amber-600 mr-2" />
                <p className="text-sm text-amber-800">
                  Has realizado cambios en el orden. Haz clic en "Guardar Orden" para aplicar los cambios.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end items-center p-6 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="btn btn-secondary-static btn-md"
            >
              Cancelar
            </button>
            
            <button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="btn btn-primary-static btn-md"
            >
              {saving ? (
                <div className="flex items-center">
                  <div className="spinner w-4 h-4 mr-2"></div>
                  Guardando...
                </div>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Guardar Orden
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MenuOrderModal
